import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { resolveUrl } from "../lib/api";
import { LogOut, ExternalLink, Menu, X } from "lucide-react";

export default function DashboardLayout({ links, groupedLinks, title, children }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const brandName = settings?.brand_name || "Inchirieri Auto Pascani";

  // Close drawer on route change
  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  // Render a single link (used in both flat & grouped modes)
  const renderLink = (l) => (
    <NavLink
      key={l.to}
      to={l.to}
      end={l.end}
      className={({ isActive }) =>
        `flex items-center gap-3 px-6 py-2.5 text-sm transition border-l-2 ${
          isActive ? "border-[var(--brand-primary)] text-[var(--brand-primary)]" : "border-transparent text-[var(--brand-text-secondary)] hover:text-[var(--brand-text)]"
        }`
      }
      data-testid={`sidebar-link-${l.key}`}
    >
      {l.icon}
      <span>{l.label}</span>
    </NavLink>
  );

  const Sidebar = ({ onClose, testid = "dashboard-sidebar", closeTestId = "close-sidebar" }) => (
    <aside
      className="w-64 border-r flex flex-col h-full"
      style={{ background: "var(--brand-surface)", borderColor: "var(--brand-border)" }}
      data-testid={testid}
    >
      <div className="px-6 py-6 border-b flex items-start justify-between" style={{ borderColor: "var(--brand-border)" }}>
        <Link to="/" className="flex items-center gap-2 flex-1 min-w-0">
          {settings?.logo_path ? (
            <img src={resolveUrl(settings.logo_path)} alt={brandName} className="h-7 w-auto shrink-0" />
          ) : null}
          {!(settings?.logo_only_mode && settings?.logo_path) && (
            <span className="font-serif text-base truncate" style={{ color: "var(--brand-primary)" }}>
              {brandName}
            </span>
          )}
        </Link>
        {onClose && (
          <button
            onClick={onClose}
            className="lg:hidden btn-ghost p-2 ml-2 shrink-0"
            aria-label="Close menu"
            data-testid={closeTestId}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="px-6 pt-3 pb-2">
        <p className="label-eyebrow">{title}</p>
      </div>
      <nav className="flex-1 py-2 overflow-y-auto">
        {groupedLinks && groupedLinks.length > 0 ? (
          groupedLinks.map((group, gi) => (
            <div key={group.key || gi} className={gi > 0 ? "mt-4" : ""} data-testid={`sidebar-group-${group.key}`}>
              <div
                className="px-6 pb-1.5 pt-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em]"
                style={{ color: "var(--brand-text-secondary)", opacity: 0.7 }}
              >
                {group.icon && <span className="opacity-80">{group.icon}</span>}
                <span>{group.label}</span>
              </div>
              {group.links.map(renderLink)}
            </div>
          ))
        ) : (
          (links || []).map(renderLink)
        )}
      </nav>
      <div className="px-6 py-4 border-t" style={{ borderColor: "var(--brand-border)" }}>
        <p className="text-xs mb-3 truncate" style={{ color: "var(--brand-text-secondary)" }}>{user?.email}</p>
        <div className="flex gap-2">
          <button
            onClick={() => navigate("/")}
            className="btn-ghost flex-1 flex items-center justify-center gap-2"
            data-testid="back-to-site"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Site
          </button>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="btn-ghost flex items-center gap-2"
            data-testid="dashboard-logout"
          >
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{ background: "var(--brand-bg)" }}>
      {/* Mobile top bar */}
      <div
        className="lg:hidden flex items-center justify-between px-4 py-3 border-b sticky top-0 z-30"
        style={{ background: "var(--brand-surface)", borderColor: "var(--brand-border)" }}
      >
        <Link to="/" className="flex items-center gap-2 min-w-0">
          {settings?.logo_path ? (
            <img src={resolveUrl(settings.logo_path)} alt={brandName} className="h-6 w-auto shrink-0" />
          ) : null}
          {!(settings?.logo_only_mode && settings?.logo_path) && (
            <span className="font-serif text-sm truncate" style={{ color: "var(--brand-primary)" }}>
              {brandName}
            </span>
          )}
        </Link>
        <div className="flex items-center gap-3">
          <span className="label-eyebrow text-[10px]">{title}</span>
          <button
            onClick={() => setOpen(true)}
            className="btn-ghost p-2"
            aria-label="Open menu"
            data-testid="open-sidebar"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:block sticky top-0 h-screen">
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="relative z-10 h-full">
            <Sidebar
              onClose={() => setOpen(false)}
              testid="dashboard-sidebar-mobile"
              closeTestId="close-sidebar-mobile"
            />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 overflow-auto">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">{children}</div>
      </main>
    </div>
  );
}
